// Deterministic Lumensurge level-up system (GDD §9.5).
// Motes collected feed cumulative thresholds; crossing one pauses the sim and
// offers 3 upgrade choices drawn from a dedicated mulberry32 stream derived
// from the run seed (never Math.random), so a fixed seed replays identically.

import type { Player, WeaponDef } from '../types.js';
import type { WorldState } from './state.js';
import { RngStream } from './rng.js';
import { LOADOUT_WEAPON_IDS, MAX_WEAPON_SLOTS } from './loadout.js';
import upgradesData from '../content/upgrades.json';
import weaponsData from '../content/weapons.json';

// ---- Content schema ----

export type UpgradeStatKind =
  | 'weapon_level'
  | 'fire_rate_mult'
  | 'damage_mult'
  | 'projectile_count'
  | 'move_speed_mult'
  | 'dash_cooldown'
  | 'max_hp'
  | 'magnet_radius_mult'
  | 'crit_chance'
  | 'shield'
  | 'hull'
  | 'laser_damage_mult'
  | 'laser_rate_mult'
  | 'laser_pierce'
  | 'projectile_pierce'
  | 'weapon_scattergun'
  | 'weapon_seeker'
  | 'weapon_waveform'
  | 'weapon_homing'
  | 'weapon_slots'
  | 'bullet_split'
  | 'dash_charges'
  | 'dash_wake'
  | 'moving_fire_rate'
  | 'serenity_damage'
  | 'hull_damage_link';

export type UpgradeRarity = 'common' | 'rare' | 'mythical';

export interface UpgradeDef {
  id: string;
  display_name: string;
  description: string;
  icon: string;
  rarity: UpgradeRarity;
  max_rank: number;
  stat: { kind: UpgradeStatKind; value: number };
}

export interface UpgradeChoice {
  def: UpgradeDef;
  /** Rank the player would gain by picking this choice (1-based). */
  nextRank: number;
}

/**
 * Aggregated passive modifiers from taken upgrades. These kinds are recorded
 * on `player.passives` and consumed here (and by game.ts for pickup radius);
 * directly-applied kinds (weapon level, max HP, dash haste) are not included.
 */
export interface UpgradeMods {
  fireRateMult: number;
  damageMult: number;
  projectileCountBonus: number;
  moveSpeedMult: number;
  magnetRadiusMult: number;
  critChanceBonus: number;
  /** Manual laser cannon modifiers (consumed by the weapon system). */
  laserDamageMult: number;
  laserRateMult: number;
  laserPierceBonus: number;
  /** Railgun Coils: extra enemies pierced by auto-weapon shots. */
  projectilePierceBonus: number;
  /** Split Rounds: projectile deaths on an enemy burst into this many extra
   * shards beyond the first (total shards = 1 + bonus). */
  bulletSplitBonus: number;
  /** Kinetic Tithe: fire-rate bonus active only while the ship flies above
   * 60% of its top speed (consumed by the weapon system). */
  movingFireRateMult: number;
  /** Stillpoint Gate: extra damage multiplier while Serenity holds tier 3
   * (consumed by getSerenityMods). */
  serenityDamageMult: number;
  /** Ablative Crown: damage bonus (additive "increased" bucket) active only
   * while the player has hull remaining (consumed by playerCombatStats). */
  hullDamageLinkMult: number;
}

const UPGRADE_DEFS = (upgradesData as { version: string; entries: UpgradeDef[] }).entries;

// Dedicated RNG stream salt: keeps upgrade rolls independent of the shared
// world streams (spawn/loot/ai/crit/vfx/audio) so offer order is stable
// regardless of how much combat RNG was consumed before a level-up.
const UPGRADE_STREAM_SALT = 0x85EBCA6B;

/** Rerolls available per run (tracked canonically on `player.rerolls_used`). */
export const MAX_REROLLS = 2;

/** Cap for weapon-level upgrades (Spear-Jelly Lance defines levels 1..5). */
export const MAX_WEAPON_LEVEL = 5;

/** Slot of the manual laser turret: always free, never an auto slot. */
export const MANUAL_TURRET_SLOT = 1;

/** Kinds applied straight onto the player rather than aggregated as passives. */
const DIRECT_KINDS: readonly UpgradeStatKind[] = [
  'weapon_level',
  'max_hp',
  'dash_cooldown',
  'shield',
  'hull',
  'weapon_scattergun',
  'weapon_seeker',
  'weapon_waveform',
  'weapon_homing',
  'weapon_slots',
  'dash_charges',
  'dash_wake'
];

/** Weapon-grant boons (spec-arsenal): stat kind → granted weapon def id. */
const WEAPON_GRANTS: Readonly<Partial<Record<UpgradeStatKind, string>>> = {
  weapon_scattergun: 'weapon.shard-scattergun',
  weapon_seeker: 'weapon.seeker-swarm',
  weapon_waveform: 'weapon.sine-caster'
};

/**
 * Hunter's Mark (weapon_homing boon): every ally projectile gains this mild
 * seeking spec. Stored duck-typed on the player as `boonHoming` — the exact
 * contract weapon-system.ts's getPlayerHomingBonus reads (kept import-free
 * to avoid the upgrade-system ↔ weapon-system cycle).
 */
const HOMING_BONUS_SPEC = { turn_rate: 90, seek_radius: 12 } as const;

const WEAPON_DEFS = (weaponsData as { version: string; entries: WeaponDef[] }).entries;

function findWeaponDef(id: string): WeaponDef {
  const def = WEAPON_DEFS.find((d) => d.id === id);
  if (!def) {
    throw new Error(`Weapon definition ${id} not found in weapons.json`);
  }
  return def;
}

/** Weapons occupying auto slots (slot 1 is the manual turret, always free). */
function autoWeapons(player: Player): Player['weapons'] {
  return player.weapons.filter((w) => w.slot !== MANUAL_TURRET_SLOT);
}

/** True while the hull has an auto weapon slot free for another grant. */
export function hasFreeAutoWeaponSlot(player: Player): boolean {
  return autoWeapons(player).length < player.weapon_slots;
}

/**
 * Equip a granted weapon in the lowest free slot number (skipping slot 1,
 * the turret's). Capacity-limited by `player.weapon_slots`: returns false
 * with no state change when every auto slot is occupied or the weapon is
 * already owned.
 */
export function grantWeapon(player: Player, weaponDefId: string): boolean {
  if (player.weapons.some((w) => w.def_id === weaponDefId)) return false;
  if (!hasFreeAutoWeaponSlot(player)) return false;
  const def = findWeaponDef(weaponDefId);
  let slot = 0;
  while (slot === MANUAL_TURRET_SLOT || player.weapons.some((w) => w.slot === slot)) slot++;
  player.weapons.push({
    def_id: def.id,
    level: 1,
    evolved: false,
    cooldown_until: 0,
    last_target_id: 0,
    policy: def.targeting_policy,
    slot
  });
  return true;
}

/**
 * Offer-eligibility predicate shared by the Lumensurge draw (and tests):
 * beyond the max-rank guard, HARDPOINT +1 hides at the slot cap, and
 * weapon-grant boons hide while every auto slot is occupied or their weapon
 * is already owned (loadout pick, earlier boon, or loot crate).
 */
export function isUpgradeOfferEligible(player: Player, def: UpgradeDef, currentRank: number): boolean {
  if (currentRank >= def.max_rank) return false;
  const { kind } = def.stat;
  if (kind === 'weapon_slots') return player.weapon_slots < MAX_WEAPON_SLOTS;
  const grantedId = WEAPON_GRANTS[kind];
  if (grantedId !== undefined) {
    if (!hasFreeAutoWeaponSlot(player)) return false;
    if (player.weapons.some((w) => w.def_id === grantedId)) return false;
  }
  return true;
}

/** Motes granted by a loot crate when no weapon/level branch applies. */
export const LOOT_CRATE_MOTES = 25;

export type LootCrateResult =
  | { result: 'weapon' | 'level'; def_id: string }
  | { result: 'motes' };

/**
 * Resolve a collected loot crate (spec: weapon → level → motes), rolling on
 * the shared 'loot' stream:
 * (a) a free auto slot plus an unowned auto weapon def grants a uniformly
 *     random unowned one at level 1;
 * (b) otherwise an owned auto weapon below MAX_WEAPON_LEVEL levels up by 1
 *     (uniformly random among the upgradable ones);
 * (c) otherwise the caller grants LOOT_CRATE_MOTES motes (the UpgradeSystem
 *     owns mote thresholds, so this function only reports the branch).
 */
export function resolveLootCrate(world: WorldState): LootCrateResult {
  const player = world.player;
  const loot = world.rng.get('loot');
  if (hasFreeAutoWeaponSlot(player)) {
    const unowned = LOADOUT_WEAPON_IDS.filter(
      (id) => !player.weapons.some((w) => w.def_id === id)
    );
    if (unowned.length > 0) {
      const defId = unowned[loot.next_int(unowned.length)]!;
      if (grantWeapon(player, defId)) return { result: 'weapon', def_id: defId };
    }
  }
  const upgradable = autoWeapons(player).filter((w) => w.level < MAX_WEAPON_LEVEL);
  if (upgradable.length > 0) {
    const weapon = upgradable[loot.next_int(upgradable.length)]!;
    weapon.level += 1;
    return { result: 'level', def_id: weapon.def_id };
  }
  return { result: 'motes' };
}

/**
 * Cumulative mote total required to reach `level` (level 2+).
 * Gaps between levels are 2×(k² − k + 4) → 10, 30, 62, 110, 178, ...
 * The first threshold (10 motes) lands inside the first ~45 s; later gaps
 * grow quadratically. Doubled from the original curve: early levels were
 * coming about twice too fast, especially with wave-density mote income.
 */
export function thresholdForLevel(level: number): number {
  let total = 10;
  for (let k = 3; k <= level; k++) {
    total += 2 * (k * k - k + 4);
  }
  return total;
}

/**
 * Apply one rank of an upgrade to the player. Pure and deterministic.
 * Exported so tests and future systems can apply a def without the full
 * offer/reroll flow.
 */
export function applyUpgradeToPlayer(player: Player, def: UpgradeDef): void {
  const { kind, value } = def.stat;
  switch (kind) {
    case 'weapon_level': {
      const weapon = player.weapons[0];
      if (weapon) weapon.level = Math.min(MAX_WEAPON_LEVEL, weapon.level + value);
      break;
    }
    case 'max_hp':
      player.max_hp += value;
      player.hp += value;
      break;
    case 'shield':
      // Grant the shield pool and fill the gained amount immediately.
      player.max_shield += value;
      player.shield += value;
      break;
    case 'hull':
      // Grant the hull pool and fill the gained amount immediately.
      player.max_hull += value;
      player.hull += value;
      break;
    case 'dash_cooldown': {
      // movement.ts reads an optional numeric `haste` field on the player;
      // cooldown ticks are divided by (1 + haste) at dash start. The galaxy
      // zone bonus is added on top each tick by game.ts.
      const p = player as unknown as { hasteFromUpgrades?: number };
      p.hasteFromUpgrades = (typeof p.hasteFromUpgrades === 'number' ? p.hasteFromUpgrades : 0) + value;
      break;
    }
    case 'weapon_scattergun':
    case 'weapon_seeker':
    case 'weapon_waveform': {
      // Weapon-grant boons (spec-arsenal): equip the weapon in the next free
      // auto slot at level 1 with the def's targeting policy. No-op when the
      // weapon is owned or every auto slot is full (offer filtering hides
      // the boon in those states; this is the belt-and-suspenders guard).
      grantWeapon(player, WEAPON_GRANTS[kind]!);
      break;
    }
    case 'weapon_homing': {
      // Hunter's Mark: all ally projectiles gain mild homing (weapon-system
      // reads `boonHoming` each tick; stronger def homing still wins).
      (player as Player & { boonHoming?: { turn_rate: number; seek_radius: number } }).boonHoming =
        { ...HOMING_BONUS_SPEC };
      break;
    }
    case 'weapon_slots':
      // HARDPOINT +1: raise the hull's auto-slot capacity, capped at 4.
      player.weapon_slots = Math.min(MAX_WEAPON_SLOTS, player.weapon_slots + value);
      break;
    case 'dash_charges':
      // TWIN FLASH: +1 flash charge per rank (movement.ts reads the bonus
      // for the pool size; charges recharge sequentially per cooldown).
      // The new charge is usable immediately — pickup feel matters.
      player.dashChargeBonus = (player.dashChargeBonus ?? 0) + value;
      player.dash.charges += value;
      break;
    case 'dash_wake':
      // PHANTOM VECTOR: dash leaves a damaging wake (enemy-ai applies it).
      player.dashWakeDamage = (player.dashWakeDamage ?? 0) + value;
      break;
    default: {
      // Aggregated passive kinds: record (or bump) the passive rank.
      const existing = player.passives.find((ps) => ps.def_id === def.id);
      if (existing) {
        existing.rank += 1;
      } else {
        player.passives.push({ def_id: def.id, rank: 1 });
      }
      break;
    }
  }
}

/** Sum passive-rank modifiers currently on the player. Deterministic. */
export function getUpgradeMods(player: Player): UpgradeMods {
  const mods: UpgradeMods = {
    fireRateMult: 0,
    damageMult: 0,
    projectileCountBonus: 0,
    moveSpeedMult: 0,
    magnetRadiusMult: 0,
    critChanceBonus: 0,
    laserDamageMult: 0,
    laserRateMult: 0,
    laserPierceBonus: 0,
    projectilePierceBonus: 0,
    bulletSplitBonus: 0,
    movingFireRateMult: 0,
    serenityDamageMult: 0,
    hullDamageLinkMult: 0
  };
  for (const passive of player.passives) {
    const def = UPGRADE_DEFS.find((d) => d.id === passive.def_id);
    if (!def || DIRECT_KINDS.includes(def.stat.kind)) continue;
    const total = def.stat.value * passive.rank;
    switch (def.stat.kind) {
      case 'fire_rate_mult': mods.fireRateMult += total; break;
      case 'damage_mult': mods.damageMult += total; break;
      case 'projectile_count': mods.projectileCountBonus += total; break;
      case 'move_speed_mult': mods.moveSpeedMult += total; break;
      case 'magnet_radius_mult': mods.magnetRadiusMult += total; break;
      case 'crit_chance': mods.critChanceBonus += total; break;
      case 'laser_damage_mult': mods.laserDamageMult += total; break;
      case 'laser_rate_mult': mods.laserRateMult += total; break;
      case 'laser_pierce': mods.laserPierceBonus += total; break;
      case 'projectile_pierce': mods.projectilePierceBonus += total; break;
      case 'bullet_split': mods.bulletSplitBonus += total; break;
      case 'moving_fire_rate': mods.movingFireRateMult += total; break;
      case 'serenity_damage': mods.serenityDamageMult += total; break;
      case 'hull_damage_link': mods.hullDamageLinkMult += total; break;
    }
  }
  return mods;
}

/**
 * Per-run Lumensurge level-up state. Owns mote progress, the pending offer,
 * per-upgrade ranks, and the reroll budget. RNG comes from a private
 * RngStream reseeded from `world.runSeed` on every reset.
 */
export class UpgradeSystem {
  private readonly world: WorldState;
  private stream: RngStream;
  private motesCollected = 0;
  private ranks = new Map<string, number>();
  private pendingOffer: UpgradeChoice[] | null = null;

  constructor(world: WorldState) {
    this.world = world;
    this.stream = new RngStream((world.runSeed ^ UPGRADE_STREAM_SALT) >>> 0);
  }

  /** Reset per-run state. Call after `world.reset(seed)` on (re)start. */
  reset(): void {
    this.stream = new RngStream((this.world.runSeed ^ UPGRADE_STREAM_SALT) >>> 0);
    this.motesCollected = 0;
    this.ranks.clear();
    this.pendingOffer = null;
  }

  getMotesCollected(): number {
    return this.motesCollected;
  }

  /** Mote total required for the next level. */
  getNextThreshold(): number {
    return thresholdForLevel(this.world.player.lumensurge_tier + 1);
  }

  hasPendingOffer(): boolean {
    return this.pendingOffer !== null && this.pendingOffer.length > 0;
  }

  getChoices(): readonly UpgradeChoice[] {
    return this.pendingOffer ?? [];
  }

  getRerollsLeft(): number {
    return Math.max(0, MAX_REROLLS - this.world.player.rerolls_used);
  }

  /** Current rank of an upgrade id (0 = not taken). */
  getRank(id: string): number {
    return this.ranks.get(id) ?? 0;
  }

  /**
   * Feed collected mote value. Crossing the next level threshold generates a
   * deterministic 3-choice offer and emits `levelup_offer`. Only one offer is
   * pending at a time; excess motes carry over to the next threshold.
   */
  addMotes(value: number, tick: number): void {
    this.motesCollected += Math.max(0, value);
    if (this.pendingOffer !== null) return;
    if (this.motesCollected < this.getNextThreshold()) return;
    this.pendingOffer = this.generateChoices();
    if (this.pendingOffer.length > 0) {
      this.world.events.emit(
        'levelup_offer',
        {
          tier: this.world.player.lumensurge_tier + 1,
          choices: this.pendingOffer.map((c) => c.def.id)
        },
        tick
      );
    }
  }

  /**
   * Reroll the pending offer. Consumes one reroll use; the new set comes from
   * the same stream, so rerolled choices are still seed-deterministic.
   * Returns false when no offer is pending or the budget is exhausted.
   */
  reroll(tick: number): boolean {
    if (this.pendingOffer === null || this.getRerollsLeft() <= 0) return false;
    this.world.player.rerolls_used += 1;
    this.pendingOffer = this.generateChoices();
    this.world.events.emit(
      'levelup_offer',
      {
        tier: this.world.player.lumensurge_tier + 1,
        choices: this.pendingOffer.map((c) => c.def.id),
        reroll: true
      },
      tick
    );
    return true;
  }

  /**
   * Apply the pending choice at `index`, advance the Lumensurge tier, and
   * clear the offer. If motes already cover the following threshold a new
   * offer is queued immediately. Returns the applied choice, or null.
   */
  choose(index: number, tick: number): UpgradeChoice | null {
    if (this.pendingOffer === null) return null;
    const choice = this.pendingOffer[index];
    if (!choice) return null;

    this.ranks.set(choice.def.id, choice.nextRank);
    applyUpgradeToPlayer(this.world.player, choice.def);
    this.world.player.lumensurge_tier += 1;
    this.pendingOffer = null;

    this.world.events.emit(
      'levelup_pick',
      {
        tier: this.world.player.lumensurge_tier,
        upgrade_id: choice.def.id,
        rank: choice.nextRank
      },
      tick
    );
    this.world.events.emit(
      'surge_change',
      { tier: this.world.player.lumensurge_tier },
      tick
    );

    // Banked motes may already cover the next level; queue its offer.
    this.addMotes(0, tick);
    return choice;
  }

  /** Draw up to 3 distinct eligible upgrades from the private stream, weighted by rarity. */
  private generateChoices(): UpgradeChoice[] {
    // isUpgradeOfferEligible: max rank plus state guards (slot capacity for
    // HARDPOINT +1 and the weapon-grant boons, ownership dup guard).
    const eligible = UPGRADE_DEFS.filter((d) =>
      isUpgradeOfferEligible(this.world.player, d, this.getRank(d.id))
    );
    const choices: UpgradeChoice[] = [];
    const count = Math.min(3, eligible.length);
    const weights: Record<UpgradeRarity, number> = { common: 60, rare: 30, mythical: 10 };
    for (let i = 0; i < count; i++) {
      let total = 0;
      for (const d of eligible) total += weights[d.rarity];
      let roll = this.stream.next() * total;
      let idx = 0;
      for (; idx < eligible.length; idx++) {
        roll -= weights[eligible[idx].rarity];
        if (roll < 0) break;
      }
      idx = Math.min(idx, eligible.length - 1);
      const def = eligible.splice(idx, 1)[0];
      choices.push({ def, nextRank: this.getRank(def.id) + 1 });
    }
    return choices;
  }
}
